import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

const STORAGE_KEY = 'mynd_tenant_id';
const USER_KEY    = 'mynd_tenant_user';

/** Persiste o tenantId no localStorage vinculado ao userId atual. */
export function persistTenantId(userId: string, tenantId: string) {
  localStorage.setItem(STORAGE_KEY, tenantId);
  localStorage.setItem(USER_KEY, userId);
}

function readStoredTenant(userId: string): string | null {
  const storedUser = localStorage.getItem(USER_KEY);
  if (storedUser !== userId) {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(USER_KEY);
    return null;
  }
  return localStorage.getItem(STORAGE_KEY);
}

export function useTenant() {
  const { user, loading: authLoading } = useAuth();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantLoading, setTenantLoading] = useState(true);

  useEffect(() => {
    // Aguarda o auth resolver antes de qualquer decisão —
    // cada instância de useAuth() começa com loading:true, e disparar
    // com user=null antes de resolver causaria redirect prematuro.
    if (authLoading) return;

    if (!user) {
      setTenantId(null);
      setTenantLoading(false);
      return;
    }

    // 1. Tenta ler do localStorage imediatamente (sem round-trip ao banco)
    const cached = readStoredTenant(user.id);
    if (cached) {
      setTenantId(cached);
      setTenantLoading(false);
      return;
    }

    // 2. Busca no banco e atualiza o cache
    supabase
      .from('tenant_members')
      .select('tenant_id')
      .eq('user_id', user.id)
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        const row = data as { tenant_id: string } | null;
        const tid = row?.tenant_id ?? null;
        if (tid) persistTenantId(user.id, tid);
        setTenantId(tid);
        setTenantLoading(false);
      });
  }, [user?.id, authLoading]);

  return { tenantId, loading: authLoading || tenantLoading };
}
